"use client";

import { OAuthForm } from "@/components/forms/oauth-form";
import { OrSeparator } from "@/components/forms/or-separator";
import { availableOAuthProviders } from "@/lib/auth/oauth-availability";
import type { OAuthFormAction, OAuthProvider } from "@/lib/auth/oauth-providers";

/** The third-party sign-in block shared by /login and /signup: every
 *  available provider's button, plus the "or" separator above the
 *  email/password form.
 *
 *  Shared rather than copy-pasted per route because WHICH providers render
 *  is an App Review compliance decision (guideline 4.8 — see
 *  availableOAuthProviders), and a second copy is a second thing to forget
 *  when that decision changes. This component is the ONE place allowed to
 *  render OAuthForm; lib/auth/oauth-render-sites.test.ts enforces that.
 *
 *  Renders nothing at all — separator included — when no provider is
 *  available, since a lone "or" above nothing reads as a rendering bug. */
export function OAuthProviderButtons({
  actions,
  verb,
  next,
  errorReturnPath,
  shell,
}: {
  /** Provider → the route's own Server Action. Server Actions can't be
   *  looked up dynamically (each is its own module export), so the caller
   *  supplies the map; `Record<OAuthProvider, …>` makes a missing entry a
   *  compile error the day a provider is added to the registry. */
  actions: Record<OAuthProvider, OAuthFormAction>;
  /** "Sign in" or "Sign up" — the provider name is appended downstream. */
  verb: string;
  next?: string;
  errorReturnPath: string;
  /** Server-detected native-shell flag, threaded from the page so the
   *  buttons are absent from the FIRST paint. A client-side check would
   *  flash them before hiding — exactly what a reviewer screenshots. */
  shell?: boolean;
}) {
  const providers = availableOAuthProviders(Boolean(shell));
  if (providers.length === 0) return null;

  return (
    <>
      <div className="space-y-2">
        {providers.map((provider) => (
          <OAuthForm
            key={provider}
            provider={provider}
            action={actions[provider]}
            verb={verb}
            next={next}
            errorReturnPath={errorReturnPath}
            shell={shell}
          />
        ))}
      </div>

      <OrSeparator />
    </>
  );
}
